import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { getAllTenants } from '../services/tenantService';
import { getTenantMenuItems, getTenantMenuCategories } from '../services/adminRestaurantService';
import { AlertCircle, Loader, Search } from 'lucide-react';
import type { Tenant } from 'foodhub-shared/types';

const MenuManagementPage: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadTenants = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAllTenants();
        setTenants(data);
        if (data.length > 0) {
          setSelectedTenant(data[0].id);
        }
      } catch (err: any) {
        console.error('Error loading tenants:', err);
        setError(err.message || 'Failed to load restaurants');
      } finally {
        setLoading(false);
      }
    };

    loadTenants();
  }, []);

  useEffect(() => {
    const loadMenuData = async () => {
      if (!selectedTenant) return;

      try {
        setLoading(true);
        setError(null);

        const [itemsData, categoriesData] = await Promise.all([
          getTenantMenuItems(selectedTenant),
          getTenantMenuCategories(selectedTenant),
        ]);

        setMenuItems(itemsData);
        setCategories(categoriesData);
      } catch (err: any) {
        console.error('Error loading menu data:', err);
        setError(err.message || 'Failed to load menu data');
      } finally {
        setLoading(false);
      }
    };

    loadMenuData();
  }, [selectedTenant]);

  const filteredItems = menuItems.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Menu Management</h1>
          <p className="text-gray-600 mt-2">Manage menus across all restaurants</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Restaurant Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Restaurant</label>
          <select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="ml-3 text-gray-600">Loading menu data...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Categories */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Categories ({categories.length})</h2>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {categories.length === 0 ? (
                  <p className="text-gray-600 text-sm">No categories</p>
                ) : (
                  categories.map((category) => (
                    <div key={category.id} className="p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                      <p className="font-medium text-gray-900">{category.name}</p>
                      <p className="text-xs text-gray-600">Order: {category.displayOrder}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Menu Items */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Menu Items ({filteredItems.length})</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search menu items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredItems.length === 0 ? (
                  <p className="text-gray-600 text-sm">No menu items found</p>
                ) : (
                  filteredItems.map((item) => (
                    <div key={item.id} className="p-4 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          <div className="flex items-center space-x-4 mt-2">
                            <span className="text-sm font-bold text-green-600">RM {item.price.toFixed(2)}</span>
                            <span className="text-xs text-gray-600">{item.category}</span>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${item.available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {item.available ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default MenuManagementPage;

